import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "motion/react";
import { Button, Field, Input, MarkLoader, rise, useToast } from "../ui/index.ts";
import { AuthFailure, configured, getSession, requestPasswordReset, setPassword, verifyRecovery } from "../lib/supabase.ts";
import { AuthShell, DEAD_LINK, problemOf, type Problem } from "./Auth.tsx";

const linkClass = "font-semibold text-primary-ink underline-offset-4 hover:underline";

function BackToLogIn() {
  return (
    <motion.p variants={rise} className="text-[15px] text-muted">
      <Link href="/login" className={linkClass}>
        Back to log in
      </Link>
    </motion.p>
  );
}

/** The email typed on the login form, which its Forgot password link carries over in history state. */
function carriedEmail(): string {
  try {
    const e = (history.state as { email?: unknown } | null)?.email;
    return typeof e === "string" ? e : "";
  } catch {
    return "";
  }
}

/**
 * Asks for a reset email. The sent state reads the same whether or not the email has an account, as Supabase's answer
 * does, so the screen can't be used to check who has one.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState(carriedEmail);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const to = email.trim();
    if (!to) return setProblem({ field: "email", text: "Enter your email." });
    setSending(true);
    setProblem(null);
    try {
      await requestPasswordReset(to);
      setSent(to);
    } catch (err) {
      setProblem(problemOf(err));
    }
    setSending(false);
  };

  if (sent)
    return (
      <AuthShell step="sent">
        <motion.div variants={rise} className="flex flex-col gap-2">
          <h1 className="font-display text-[34px] leading-[1.08] text-ink">Check your email.</h1>
          <p className="text-pretty text-[15px] text-muted">
            If an account exists for <span className="font-semibold text-ink">{sent}</span>, we sent a link to reset your password.
          </p>
        </motion.div>
        <BackToLogIn />
      </AuthShell>
    );

  return (
    <AuthShell step="ask">
      <motion.div variants={rise} className="flex flex-col gap-2">
        <h1 className="font-display text-[34px] leading-[1.08] text-ink">Reset your password.</h1>
        <p className="text-pretty text-[15px] text-muted">Enter the email for your headcount account and we'll send you a link to reset your password.</p>
      </motion.div>
      <motion.form variants={rise} className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label="Email" error={problem?.field === "email" ? problem.text : undefined}>
          <Input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </Field>
        {problem && problem.field !== "email" && (
          <p className="text-sm text-bad-ink" role="alert">
            {problem.text}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-1" loading={sending} disabled={!configured}>
          Send reset link
        </Button>
      </motion.form>
      <BackToLogIn />
    </AuthShell>
  );
}

type ResetLink = { tokenHash: string | null; code: boolean; dead: boolean };

/**
 * What the reset email's link brought: a `token_hash` from headcount's template, or a `?code=` from Supabase's default
 * one, which supabase-js exchanges on load. An `error_code` in the query or hash means Supabase already refused the link.
 */
function readLink(): ResetLink {
  const q = new URLSearchParams(location.search);
  const h = new URLSearchParams(location.hash.slice(1));
  const tokenHash = q.get("type") === "recovery" || !q.has("type") ? q.get("token_hash") : null;
  return { tokenHash, code: q.has("code"), dead: q.has("error_code") || h.has("error_code") || q.has("error") || h.has("error") };
}

/**
 * Sets a new password from the reset email's link, signed in or out. The token is spent only on submit, then the new
 * password is saved and the user lands in the app signed in. A used or expired link offers a new one.
 */
export function ResetPassword() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [link] = useState(readLink);
  const [stage, setStage] = useState<"checking" | "form" | "dead">(() => (link.dead ? "dead" : link.tokenHash ? "form" : link.code ? "checking" : "dead"));
  const [verified, setVerified] = useState(false);
  const [password, setPasswordValue] = useState("");
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (stage !== "checking") return;
    let alive = true;
    getSession().then(
      (s) => {
        if (!alive) return;
        setVerified(Boolean(s));
        setStage(s ? "form" : "dead");
      },
      () => alive && setStage("dead"),
    );
    return () => {
      alive = false;
    };
  }, [stage]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!password) return setProblem({ field: "password", text: "Enter a new password." });
    if (password.length < 8) return setProblem({ field: "password", text: "Use at least 8 characters." });
    setSaving(true);
    setProblem(null);
    try {
      if (!verified && link.tokenHash) {
        await verifyRecovery(link.tokenHash);
        setVerified(true);
      }
      await setPassword(password);
      toast("Your password has been reset.", "ok");
      navigate("/", { replace: true });
      return;
    } catch (err) {
      if (err instanceof AuthFailure && DEAD_LINK.has(err.code)) setStage("dead");
      else setProblem(problemOf(err));
    }
    setSaving(false);
  };

  if (stage === "checking")
    return (
      <div className="grid min-h-dvh place-items-center bg-page">
        <MarkLoader delay={0.4} />
      </div>
    );

  if (stage === "dead")
    return (
      <AuthShell step="dead">
        <motion.div variants={rise} className="flex flex-col gap-2">
          <h1 className="text-balance font-display text-[34px] leading-[1.08] text-ink">This link is invalid or has expired.</h1>
          <p className="text-pretty text-[15px] text-muted">Request a new link to reset your password.</p>
        </motion.div>
        <motion.div variants={rise} className="flex flex-col">
          <Button size="lg" onClick={() => navigate("/forgot-password")}>
            Request a new link
          </Button>
        </motion.div>
        <BackToLogIn />
      </AuthShell>
    );

  return (
    <AuthShell step="form">
      <motion.div variants={rise} className="flex flex-col gap-2">
        <h1 className="font-display text-[34px] leading-[1.08] text-ink">Set a new password.</h1>
        <p className="text-pretty text-[15px] text-muted">Enter a new password for your headcount account.</p>
      </motion.div>
      <motion.form variants={rise} className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label="New password" error={problem?.field === "password" ? problem.text : undefined}>
          <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPasswordValue(e.target.value)} autoFocus />
        </Field>
        {problem && problem.field !== "password" && (
          <p className="text-sm text-bad-ink" role="alert">
            {problem.text}
          </p>
        )}
        <Button type="submit" size="lg" className="mt-1" loading={saving} disabled={!configured}>
          Reset password
        </Button>
      </motion.form>
    </AuthShell>
  );
}
